import { useCallback, useEffect, useRef } from 'react';

type UseTimeoutFnReturn = [() => boolean | undefined, () => void, () => void];

export default function useTimeoutFn(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  fn: Function,
  ms: number = 0,
): UseTimeoutFnReturn {
  const ready = useRef<boolean | undefined>(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const callback = useRef(fn);

  const isReady = useCallback(() => ready.current, []);

  const set = useCallback(() => {
    ready.current = false;
    if (timeout.current) clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      ready.current = true;
      callback.current();
    }, ms);
  }, [ms]);

  const clear = useCallback(() => {
    ready.current = undefined;
    if (timeout.current) clearTimeout(timeout.current);
  }, []);

  // update ref when function changes
  useEffect(() => {
    callback.current = fn;
  }, [fn]);

  // set on mount, clear on unmount
  useEffect(() => {
    set();

    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms]);

  return [isReady, clear, set];
}
